import sdk from '@service/auth-sdk'
import {
  emptyPdfGeoref,
  isCompletePdfGeoref,
  looksLikeGeoPdf,
  serializePdfGeorefs,
} from '@utils/geo-pdf'
import RasterDatastoreHandler from './RasterDatastoreHandler'

export default class PdfDatastoreHandler extends RasterDatastoreHandler {
  constructor() {
    super('pdf')
    this.inspectSeq = 0
  }

  applyTypeDefaults(data, set) {
    super.applyTypeDefaults(data, set)
    set('pdf_georefs', {})
    set('pdf_inspecting', false)
  }

  prepareFormData(data) {
    super.prepareFormData(data)
    data.pdf_georefs = data.pdf_georefs || {}
    data.pdf_inspecting = !!data.pdf_inspecting
    return data
  }

  georefFor(data, name) {
    const map = (data && data.pdf_georefs) || {}
    return map[name] || emptyPdfGeoref()
  }

  isFileReady(data, file) {
    return isCompletePdfGeoref(this.georefFor(data, file && file.name))
  }

  regularPdfFiles(data) {
    return this.selectedFiles(data).filter((file) => {
      const row = data.pdf_georefs && data.pdf_georefs[file.name]
      return row && !row.is_geopdf
    })
  }

  fileState(file, data) {
    const row = data && data.pdf_georefs && data.pdf_georefs[file.name]
    if (!row) {
      return { label: 'Đang kiểm tra...' }
    }
    if (row.is_geopdf) {
      return { label: 'GeoPDF' }
    }
    if (isCompletePdfGeoref(row)) {
      return { label: 'Đã nhập tọa độ', georef: true }
    }
    return { error: true, georef: true, label: 'PDF thường · Thiếu tọa độ' }
  }

  validateCreate(data) {
    if (data.pdf_inspecting) {
      return {
        ok: false,
        errors: { file: ['Đang kiểm tra file PDF, vui lòng đợi'] },
      }
    }
    const missing = this.selectedFiles(data).filter(
      (file) => !this.isFileReady(data, file)
    )
    if (missing.length) {
      return {
        ok: false,
        errors: {
          file: [
            'Vui lòng nhập hệ tọa độ và vùng bao cho file PDF thường trước khi tạo bộ dữ liệu',
          ],
        },
      }
    }
    return { ok: true, errors: {} }
  }

  buildCreatePayload(data, context = {}) {
    const payload = super.buildCreatePayload(data, context)
    payload.pdf_georefs = JSON.stringify(serializePdfGeorefs(data.pdf_georefs))
    return payload
  }

  async inspectSelectedFiles(data, { set, del } = {}) {
    const seq = ++this.inspectSeq
    const files = this.selectedFiles(data)
    if (!data.pdf_georefs) set(data, 'pdf_georefs', {})
    const map = data.pdf_georefs
    Object.keys(map || {}).forEach((name) => {
      if (!files.some((file) => file.name === name) && del) {
        del(map, name)
      }
    })
    const pending = files.filter((file) => !map[file.name])
    if (!pending.length) {
      set(data, 'pdf_inspecting', false)
      return false
    }
    set(data, 'pdf_inspecting', true)
    await Promise.all(
      pending.map(async (file) => {
        const isGeo = await looksLikeGeoPdf(file)
        if (seq !== this.inspectSeq) return
        set(
          map,
          file.name,
          Object.assign({}, emptyPdfGeoref(), { is_geopdf: isGeo })
        )
      })
    )
    if (seq !== this.inspectSeq) return false
    set(data, 'pdf_inspecting', false)
    return true
  }

  parseUploadMeta(data) {
    if (!(data instanceof FormData)) return {}
    const raw = data.get('pdf_georefs')
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {}
    } catch (error) {
      return {}
    }
  }

  appendUploadedFile(formData, index, item, meta = {}) {
    super.appendUploadedFile(formData, index, item)
    const georef = (item && item.georef) || meta[item && item.file_name]
    if (!georef) return
    if (georef.is_geopdf) {
      formData.append(`files[${index}][is_geopdf]`, '1')
      return
    }
    if (!isCompletePdfGeoref(georef)) return
    formData.append(`files[${index}][srs]`, georef.srs)
    formData.append(`files[${index}][min_x]`, georef.min_x)
    formData.append(`files[${index}][min_y]`, georef.min_y)
    formData.append(`files[${index}][max_x]`, georef.max_x)
    formData.append(`files[${index}][max_y]`, georef.max_y)
  }

  async inspectUploaded(uploaded) {
    try {
      const res = await sdk.request({
        method: 'post',
        url: 'datastores/inspect-pdf',
        data: {
          files: (uploaded || []).map((item) => ({
            file_path: item.file_path,
            file_name: item.file_name,
          })),
        },
      })
      const body = (res && res.data) || {}
      const inner = body.data || body
      return Array.isArray(inner.files) ? inner.files : []
    } catch (error) {
      return (uploaded || []).map((item) => ({
        file_path: item.file_path,
        file_name: item.file_name,
        georeferenced: false,
      }))
    }
  }

  async afterUpload({
    uploaded,
    data,
    promptGeoref,
    onStatus,
  }) {
    const provided = this.parseUploadMeta(data)
    const pending = (uploaded || []).filter(
      (item) => !isCompletePdfGeoref(provided[item.file_name] || item.georef)
    )
    if (pending.length) {
      if (onStatus) onStatus('inspect', 'Đang kiểm tra tọa độ PDF...')
      const inspectFiles = await this.inspectUploaded(pending)
      const stillNeed = inspectFiles.filter((file) => {
        if (file.georeferenced) return false
        return !isCompletePdfGeoref(provided[file.file_name])
      })
      if (stillNeed.length) {
        if (onStatus) onStatus('georef', 'Chờ nhập tọa độ')
        if (!promptGeoref) {
          throw new Error('missing-georef')
        }
        const extra = await promptGeoref({
          files: stillNeed,
          prefill: provided,
        })
        Object.keys(extra || {}).forEach((name) => {
          provided[name] = extra[name]
        })
      }
    }
    return (uploaded || []).map((item) =>
      Object.assign({}, item, {
        georef: provided[item.file_name] || item.georef || null,
      })
    )
  }
}
